import {
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import { modalTypes } from "constants/types";
import { ReactComponent as AddIllustration } from "icons/addIllustration.svg";
import { ReactComponent as FromOppDisabledIllustration } from "icons/fromOppDisabledIllustration.svg";
import { ReactComponent as FromOppIllustration } from "icons/fromOppIllustration.svg";
import { ReactComponent as SearchIllustration } from "icons/searchIllustration.svg";
import { Info } from "lucide-react";
import { func } from "prop-types";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { hideModal, showModal } from "slices/app/actions/modals";
import { fetchTrackedOppsCount } from "slices/opportunity/tracked/actions";
import { selectTotalCount } from "slices/opportunity/tracked/selectors";

import Flex, { GAP_CLASS } from "../../Flex";
import { ALIGN_CLASS, DIRECTION_CLASS } from "../../Flex/Flex";
import ChooserCard from "../ChooserCard";
import { goToOpportunitySearchModal } from "../confModalTypes";
import classes from "./NewProposalChooserModal.module.scss";

const NewProposalChooserModal = ({ onAddManually }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const trackedTotalCount = useSelector(selectTotalCount);
  const disabled = trackedTotalCount === 0;

  useEffect(() => {
    dispatch(fetchTrackedOppsCount());
  }, [dispatch]);

  const handleClose = () =>
    dispatch(hideModal(modalTypes.NEW_PROPOSAL_CHOOSER));

  const handleFromTracked = () => {
    dispatch(hideModal(modalTypes.NEW_PROPOSAL_CHOOSER));
    dispatch(
      showModal(modalTypes.TRACKED_OPPORTUNITY_PICKER, {
        onBack: () => {
          dispatch(hideModal(modalTypes.TRACKED_OPPORTUNITY_PICKER));
          dispatch(
            showModal(modalTypes.NEW_PROPOSAL_CHOOSER, { onAddManually })
          );
        },
      })
    );
  };

  const handleFindNew = () => {
    dispatch(hideModal(modalTypes.NEW_PROPOSAL_CHOOSER));
    dispatch(
      showModal(
        modalTypes.CONFIRMATION,
        goToOpportunitySearchModal(
          dispatch,
          navigate,
          onAddManually,
          modalTypes.NEW_PROPOSAL_CHOOSER
        )
      )
    );
  };

  const handleAddManually = () => {
    dispatch(hideModal(modalTypes.NEW_PROPOSAL_CHOOSER));
    onAddManually();
  };

  return (
    <Modal
      data-testid="new-proposal-chooser-modal"
      className={`fade-in ${classes.modal}`}
      show
      centered
      backdrop
      onDismiss={handleClose}
    >
      <Modal.Header>New Proposal</Modal.Header>

      <Modal.Body>
        <Flex
          direction={DIRECTION_CLASS.column}
          gap={GAP_CLASS.GAP_200}
          alignItems={ALIGN_CLASS["flex-start"]}
        >
          <p className={classes.subtitle}>
            Every proposal must link to an opportunity. Pick one, find a new
            one, or add it manually.
          </p>

          <div className={classes.cards}>
            <ChooserCard
              ariaLabel="From a Tracked Opportunity"
              illustration={
                disabled ? FromOppDisabledIllustration : FromOppIllustration
              }
              title="From a Tracked Opportunity"
              subtitle="You're already tracking it"
              onClick={handleFromTracked}
              disabled={disabled}
              tooltip="You have no tracked opportunities"
              recommended
            />
            <ChooserCard
              ariaLabel="Find a New Opportunity"
              illustration={SearchIllustration}
              title="Find a New Opportunity"
              subtitle="Search SAM.gov, eBuy & more"
              onClick={handleFindNew}
            />
            <ChooserCard
              ariaLabel="Add Manually"
              illustration={AddIllustration}
              title="Add Manually"
              subtitle="Enter the details yourself"
              onClick={handleAddManually}
            />
          </div>
          {disabled && (
            <Flex gap={GAP_CLASS.GAP_150} className={classes.hint}>
              <Info size={16} className={classes.infoIcon} />
              In order to use this option, you must have a tracked opp.
            </Flex>
          )}
        </Flex>
      </Modal.Body>

      <Modal.Footer>
        <NewButton variant={NewButtonVariant.l3} onClick={handleClose}>
          Cancel
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

NewProposalChooserModal.propTypes = {
  onAddManually: func.isRequired,
};

export default NewProposalChooserModal;
